import { BreakpointObserver, Breakpoints, MediaMatcher } from '@angular/cdk/layout';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { map, Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SrpPlatformService {
    screenUltraSmallBreakpoint = 420;
    screenSmallBreakpoint = 560;
    screenTabBreakpoint = 768;
    screenBigTabBreakpoint = 1024;
    screenMediumBreakpoint = 1280;
    screenLargeBreakpoint = 1920;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private _media: MediaMatcher,
        private _breakpointObserver: BreakpointObserver,
    ) { }

    get isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    get isServer(): boolean {
        return isPlatformServer(this.platformId);
    }

    get screenUltraSmall(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenUltraSmallBreakpoint;
    }

    screenUltraSmall$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenUltraSmallBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenSmall(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenSmallBreakpoint;
    }

    screenSmall$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenSmallBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenTab(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenTabBreakpoint;
    }

    screenTab$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenTabBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenBigTab(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenBigTabBreakpoint;
    }

    screenBigTab$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenBigTabBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenMedium(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenMediumBreakpoint;
    }

    screenMedium$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenMediumBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenLarge(): boolean {
        if (!this.isBrowser) {
            return true;
        }
        return (
            window.innerWidth <= this.screenLargeBreakpoint &&
            window.innerWidth > this.screenMediumBreakpoint
        );
    }

    screenLarge$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(true);
        }
        return this._breakpointObserver
            .observe(`(min-width: ${this.screenMediumBreakpoint}px) and (max-width: ${this.screenLargeBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenExtraLarge(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth > this.screenLargeBreakpoint;
    }

    screenExtraLarge$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(min-width: ${this.screenLargeBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenSmallLarge(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth < this.screenLargeBreakpoint;
    }

    screenSmallLarge$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenLargeBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get isLandscapeMode(): boolean {
        return this._media.matchMedia('(orientation: landscape)').matches;
    }

    isLandscapeMode$(): Observable<boolean> {
        return this._breakpointObserver
            .observe([
                Breakpoints.HandsetLandscape,
                Breakpoints.TabletLandscape,
                Breakpoints.WebLandscape,
            ])
            .pipe(map(x => x.matches));
    }

    get isTouchScreen(): boolean {
        if (!this.isBrowser) return false;
        return (
            ('ontouchstart' in window) || (navigator['msMaxTouchPoints'] > 0)
        );
    }

    get isSupportVibration(): boolean {
        return 'vibrate' in navigator;
    }

    browsers = {
        isIPhone: /iPhone/.test(navigator.platform),
        isIOs: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isNewIPadOs: /Macintosh/.test(navigator.userAgent) && this.isTouchScreen,
        isAndroid: /Android/i.test(navigator.userAgent),
        isSafari: /^((?!chrome).)*safari.*$/i.test(navigator.userAgent),
        isChromium: /Chrome/.test(navigator.userAgent),
        isFirefox: /Firefox\/([0-9]+)\./i.test(navigator.userAgent),
    };
}
